import { Link } from 'react-router-dom'
import { PublicLayout } from '../components/layout/PublicLayout'
import { Calendar, Users, BarChart3, ArrowRight, Check } from 'lucide-react'

const BENEFITS = [
  {
    icon: Calendar,
    title: 'Import your existing events in 2 minutes',
    body: 'Paste your Google Calendar iCal link and we\'ll pull in all your upcoming events automatically — no manual re-entry.',
  },
  {
    icon: Users,
    title: 'Reach people beyond your bulletin board',
    body: 'Your events appear in Fellowship\'s discovery feed, putting them in front of congregation members actively looking for gatherings nearby.',
  },
  {
    icon: BarChart3,
    title: 'See who\'s finding your church',
    body: 'A simple traffic dashboard shows how many people are viewing your events and planning to attend — useful data at budget time.',
  },
]

const STEPS = [
  'Find your church in our directory (we may already have a listing)',
  'Claim it with a free account — no credit card needed',
  'Import your events from Google Calendar or add them manually',
  'Your 14-day free trial starts the moment you enable RSVPs',
]

export default function ForChurchesPage() {
  return (
    <PublicLayout>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div className="max-w-[1080px] mx-auto px-6 pt-16 pb-12">
        <div className="max-w-[640px]">
          <div className="inline-block text-[11px] font-semibold uppercase tracking-[0.1em] text-primary bg-primary-soft border border-primary rounded-full px-3 py-1 mb-5">
            For churches
          </div>
          <h1 className="serif text-[52px] leading-[1.05] tracking-[-0.025em] text-ink m-0 mb-5">
            Put your church<br />
            <span className="serif-italic text-primary">on the map</span>
          </h1>
          <p className="text-[17px] text-ink-2 leading-[1.65] m-0 mb-8 max-w-[520px]">
            Fellowship helps your congregation discover and RSVP to your events — from potlucks to Bible studies to service projects. Import your existing calendar in minutes, not hours.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to="/churches"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-primary text-on-primary text-[15px] font-semibold no-underline"
            >
              Find your church <ArrowRight size={16} />
            </Link>
            <Link
              to="/churches/new"
              className="inline-flex items-center h-12 px-5 rounded-xl border border-line text-ink text-[14px] font-medium no-underline"
            >
              Add your church
            </Link>
          </div>
          <p className="text-[12.5px] text-muted mt-3">
            14-day free trial &middot; No credit card required to claim your listing
          </p>
        </div>
      </div>

      {/* ── Benefits ──────────────────────────────────────────────────────── */}
      <div className="bg-s2 border-t border-b border-line-soft py-14">
        <div className="max-w-[1080px] mx-auto px-6">
          <div className="eyebrow text-center mb-10">Why Fellowship</div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {BENEFITS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-surface border border-line-soft rounded-2xl p-6">
                <div className="w-10 h-10 rounded-xl bg-primary-soft flex items-center justify-center mb-4">
                  <Icon size={18} className="text-primary" />
                </div>
                <div className="text-[15px] font-semibold text-ink mb-2 leading-[1.3]">{title}</div>
                <p className="text-[13px] text-ink-2 leading-[1.6] m-0">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── How it works ──────────────────────────────────────────────────── */}
      <div className="max-w-[680px] mx-auto px-6 py-14">
        <div className="eyebrow text-center mb-8">How it works</div>
        <div className="flex flex-col gap-4">
          {STEPS.map((step, i) => (
            <div key={i} className="flex gap-3 items-start">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-on-primary text-[11px] font-bold flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <p className="text-[14px] text-ink-2 leading-[1.6] m-0">{step}</p>
            </div>
          ))}
        </div>

        {/* ── CTA card ────────────────────────────────────────────────────── */}
        <div className="mt-10 rounded-2xl bg-primary-soft border border-primary p-7 text-center">
          <h2 className="serif text-[26px] text-ink m-0 mb-2">Ready to get started?</h2>
          <p className="text-[14px] text-ink-2 m-0 mb-5">
            We may already have a listing for your church. Search the directory to find out.
          </p>
          <Link
            to="/churches"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-primary text-on-primary text-[14px] font-semibold no-underline"
          >
            Find your church <ArrowRight size={15} />
          </Link>
        </div>

        {/* ── Trust line ──────────────────────────────────────────────────── */}
        <div className="mt-8 flex flex-col gap-2">
          {['Free to claim your listing', 'Import Google Calendar events with one URL', '14-day free trial, cancel anytime'].map((line) => (
            <div key={line} className="flex items-center gap-2 text-[13px] text-ink-2">
              <Check size={14} className="text-primary shrink-0" />
              {line}
            </div>
          ))}
        </div>
      </div>
    </PublicLayout>
  )
}
